import { useContext, useEffect, useState } from "react";
import { AircraftWarrantyContext } from "../../contexts/AircraftWarrantyContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import MultioptionButton from "../../../../components/Buttons/MultioptionButton";
import "./AircraftBalanceConfiguration.css";
import { Dropdowns } from "../../../../components/Dropdowns/Dropdowns";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import Input from "../../../../components/Inputs/Input";
import { AircraftBalanceContext } from "../../contexts/AircraftBalanceContext";
import {
  localStorageGet,
  localStorageSave,
} from "../../../../services/localStorageService";
import {
  getBalanceAircraft,
  updateAircraftBalance,
} from "../../services/AircraftConfigurationService";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { HTTP_RESPONSE_CODES } from "../../../../globals/globals";
const localStoragePrefix = "configuration/aircraft/balance/draft/general";
const ribbonColors = ["var(--blue-300)", "var(--green-350)", "#f7931e"];
const dropdownNames = {
  41: "weightUnitId",
  42: "volumeUnitId",
  43: "momentUnitId",
};

const hideFieldsWhenDisabled = new Set([
  "Weight Unit",
  "Volume Unit",
  "Moment Unit",
]);
const defaultEmptyValues = {
  maxTaxiWeight: "",
  maxLandingWeight: "",
  maxTakeoffWeight: "",
  maxZeroFuelWeight: "",
  basicEmptyWeight: "",
  fuelCapacity: "",
  totalMoment: "",
  weightUnitId: null,
  volumeUnitId: null,
  momentUnitId: null,
};
const AircraftBalanceConfiguration = ({ aircraft, disabled, ribbon, open }) => {
  const {
    dropdownsData,
    setDropdownsData,
    setDropdownIds,
    dropdownDefaultOptions,
  } = useContext(DropdownsContext);
  const [backup, setBackup] = useState(null);
  const [values, setValues] = useState({ ...defaultEmptyValues });
  const [defaults, setDefaults] = useState({});
  const [partBackup, setPartBackup] = useState(null);
  const { hasPermission } = usePermissions();

  const { successMsg, warningMsg, infoMsg } = useSnackBar();
  const {
    saveDraft,
    removeDraft,
    resetType,
    newDrafts,
    isAircraftSectionDraft,
    updateSection,
  } = useContext(AircraftBalanceContext);
  useEffect(() => {
    if (disabled && typeof values.id === "number") {
      localStorageSave(localStoragePrefix, { id: aircraft.id }, values);
    }
  }, [values]);
  const handleDropdownValue = (value, name, data) => {
    changeValue({ target: { name, value: value[data], completeValue: value } });

    setDefaults((prev) => ({
      ...prev,
      ...{ [name]: " " },
    }));
  };
  useEffect(() => {
    if (open) {
      getBalanceAircraft(aircraft.id)
        .then((response) => {
          if (response.data.responseCode === 200) {
            if (response.data.load != null) {
              let {
                id,
                maxTaxiWeight,
                maxLandingWeight,
                maxTakeoffWeight,
                maxZeroFuelWeight,
                basicEmptyWeight,
                fuelCapacity,
                totalMoment,
                version,
                weightUnitId,
                volumeUnitId,
                momentUnitId,
              } = response.data.load;
              setPartBackup(response.data.load);
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                id,
                maxTaxiWeight: maxTaxiWeight,
                maxLandingWeight: maxLandingWeight,
                maxTakeoffWeight: maxTakeoffWeight,
                maxZeroFuelWeight: maxZeroFuelWeight,
                basicEmptyWeight: basicEmptyWeight,
                fuelCapacity: fuelCapacity,
                totalMoment: totalMoment,
                weightUnitId: weightUnitId,
                volumeUnitId: volumeUnitId,
                momentUnitId: momentUnitId,
                version: version,
                aircraftId: { id: aircraft.id },
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
              setBackup(form);
            } else {
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                aircraftId: aircraft.id,
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
            }
          }
        })
        .catch((error) => {
          console.error(error);
          let form = { ...defaultEmptyValues };
          setDefaults(form);
          setValues(form);
        });
    }
  }, [open, aircraft]);
  const updateDropdown = (dropdownId, parentValues, dependenciesToUpdate) => {
    setDropdownsData((prevDropdowns) =>
      prevDropdowns.map((filter) => {
        if (filter.id === dropdownId) {
          return {
            ...filter,
            parentValues,
            dependenciesToUpdate,
            reload: true,
          };
        }
        return filter;
      })
    );
  };
  const changeValue = (e) => {
    const { name, value, completeValue } = e.target;
    setValues((prev) => {
      return {
        ...prev,
        ...{ [name]: value },
      };
    });
  };
  const resetDropdownsReload = () => {
    setDropdownsData((filterState) => {
      if (filterState.length > 0) {
        return filterState.map((e) => {
          if (e.reload) {
            return { ...e, reload: false };
          }
          return e;
        });
      }
      return filterState;
    });
  };
  const cancelEdit = () => {
    removeDraft(aircraft.id, "general");
    setValues(backup ?? defaultEmptyValues);
    setDefaults(backup ?? defaultEmptyValues);
  };
  const saveSection = (disableForm) => {
    infoMsg("Saving...");
    // let draft = JSON.parse(localStorage.getItem(`${localStoragePrefix}?id=${part.id}`) ?? "{}");
    let draft = localStorageGet(localStoragePrefix, {
      id: aircraft.id.toString(),
    })[0];

    draft.aircraftId = { id: aircraft.id };

    updateAircraftBalance(draft)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.UPDATED) {
          successMsg("Aircraft saved successfully");
          setValues({ ...defaultEmptyValues, ...response.data.load });
          setBackup({ ...defaultEmptyValues, ...response.data.load });
          if (disableForm) {
            removeDraft(aircraft.id, "general");
          }
        } else if (
          response.data.responseCode === HTTP_RESPONSE_CODES.VALIDATIONS_FAILED
        ) {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            "One or more fields are invalid.",
            30000
          );
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            100000
          );
        }
      })
      .catch((error) => warningMsg(`Something went wrong: ${error}`));
  };
  const generalSectionButtons = [
    {
      label: "Save and Finish",
      onClick: () => saveSection(true),
      icon: "icon-save",
      id: "save",
    },
    {
      label: "Save and Continue Editing",
      onClick: () => saveSection(false),
      icon: "icon-save",
      id: "save-continue",
    },
    {
      label: "Cancel",
      onClick: () => cancelEdit(),
      icon: "icon-cross",
      id: "cancel",
      type: "danger",
    },
  ];
  const doNothing = () => {};

  return (
    <div className="general-aircraft-section-form-wrapper">
      <div className="general-section-aircraft-form-component">
        <div className="general-section-aircraft-form-header subTitle">
          {ribbon && (
            <div className="ribbons-container">
              {ribbon
                .filter?.((r) => r.when)
                ?.map?.((r, i, a) => (
                  <span
                    className="ribbon"
                    key={r.label}
                    style={{
                      backgroundColor:
                        r.color ?? ribbonColors[i % ribbonColors.length],
                      left:
                        i < a.length - 1
                          ? `${-0.5 * (ribbon.length - 1 - i)}rem`
                          : "",
                    }}
                  >
                    {r.label}
                  </span>
                ))}
            </div>
          )}
          <div className="title-container">
            <b>Weight and Balance</b>
          </div>
          <div className="general-info-actions">
            {!disabled && hasPermission("update") && (
              <button
                className="outlined iconTextButton small-size"
                onClick={() => saveDraft(aircraft.id, "general", values)}
              >
                <i className="icon-edit"></i>
                <span>Edit</span>
              </button>
            )}
            {disabled && hasPermission("update") && (
              <MultioptionButton buttons={generalSectionButtons} />
            )}
          </div>
        </div>
        <div className="general-aircraft-balance">
          <b className="subTitle">Weight</b>
          <div className="general-aircraft-form first-row">
            <Dropdowns
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[41])}
              disabled={!disabled}
              key={41}
              onOptionSelect={(value, data) =>
                handleDropdownValue(value, dropdownNames[41], data)
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 41)}
              preSelectValue={defaults?.[dropdownNames[41]]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="maxTaxiWeight"
              disableAutocomplete={true}
              isNumeric={true}
              label={"Max Taxi Weight (UOM)"}
              value={values["maxTaxiWeight"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="maxLandingWeight"
              disableAutocomplete={true}
              isNumeric={true}
              label={"Max Landing Weight"}
              value={values["maxLandingWeight"]}
            />
          </div>
          <div className="general-aircraft-form subTitle">
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="maxTakeoffWeight"
              disableAutocomplete={true}
              isNumeric={true}
              label={"Max Takeoff Weight"}
              value={values["maxTakeoffWeight"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              disableAutocomplete={true}
              name="maxZeroFuelWeight"
              isNumeric={true}
              label={"Max Zero Fuel Weight"}
              value={values["maxZeroFuelWeight"]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              disableAutocomplete={true}
              name="basicEmptyWeight"
              label={"Basic Empty Weight"}
              isNumeric={true}
              value={values["basicEmptyWeight"]}
            />
          </div>
          <b className="subTitle">Fuel</b>
          <div className="general-aircraft-form first-row subTitle">
            <Dropdowns
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              display1Grid={true}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[42])}
              disabled={!disabled}
              key={42}
              onOptionSelect={(value, data) =>
                handleDropdownValue(value, dropdownNames[42], data)
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 42)}
              preSelectValue={defaults?.[dropdownNames[42]]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="fuelCapacity"
              disableAutocomplete={true}
              isNumeric={true}
              label={"Fuel Capacity"}
              value={values["fuelCapacity"]}
            />
          </div>
          <b>Moment</b>
          <div className="general-aircraft-form first-row">
            <Dropdowns
              style={{ gridColumn: "span 1" }}
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              display1Grid={true}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[43])}
              disabled={!disabled}
              key={43}
              onOptionSelect={(value, data) =>
                handleDropdownValue(value, dropdownNames[43], data)
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 43)}
              preSelectValue={defaults?.[dropdownNames[43]]}
            />
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={!disabled}
              onChange={changeValue}
              name="totalMoment"
              disableAutocomplete={true}
              isNumeric={true}
              label={"Total Moment"}
              value={values["totalMoment"]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftBalanceConfiguration;
